// The warm-up on a cold start (a real page load; a return to an app still in memory shows nothing:
// the work is done). main.tsx arms it, the update gate starts it once its check is over.
import { timed } from '../../lib/perf';
import { store } from '../../state/store';
import { setWarmupView, warmupView } from '../startup-gates';
import { WARMUP_STEPS } from './registry';
import { runWarmup } from './run';
import type { WarmupStep } from './step';

let appReady: Promise<void> = Promise.resolve();

/** Before the first render: the loading screen is up from the first frame. Once per page. */
export function armWarmup(ready: Promise<unknown>): void {
  if (warmupView().state !== 'off') return;
  // the data are in once the app is 'ready' (initApp may still ask the server who is signed in), or
  // initApp gave up (then the dictionary step finds no dictionary and the app goes on as before)
  appReady = Promise.race([
    ready.then(
      () => undefined,
      () => undefined,
    ),
    new Promise<void>((resolve) => {
      if (store.get().phase === 'ready') return resolve();
      const off = store.subscribe(() => {
        if (store.get().phase !== 'ready') return;
        off();
        resolve();
      });
    }),
  ]);
  setWarmupView({ state: 'armed', label: null, fraction: 0 });
}

/**
 * One frame for the screen to paint, then on (a hidden page has no frames: a timer then, so a start
 * in the background still finishes).
 */
export function pauseForPaint(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      setTimeout(resolve, 0);
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(go);
    setTimeout(go, 100);
  });
}

export function stepsHere(steps: readonly WarmupStep[], dev: boolean): WarmupStep[] {
  return dev ? steps.filter((s) => s.dev !== false) : [...steps];
}

/** After the update check (no new version to reload into): run every step. No-op unless armed. */
export function startWarmup(): void {
  if (warmupView().state !== 'armed') return;
  setWarmupView({ state: 'running', label: null, fraction: 0 });
  void runWarmup(stepsHere(WARMUP_STEPS, import.meta.env.DEV), {
    pause: pauseForPaint,
    appReady,
    measure: (id, f) => timed(`warmup-${id}`, f),
    onProgress: (p) => setWarmupView({ state: p.done ? 'done' : 'running', label: p.label, fraction: p.fraction }),
  }).catch((err: unknown) => {
    // the runner itself never throws (a failed step is done); a broken screen must not hold the app
    console.warn('[warmup] failed', err);
    setWarmupView({ state: 'done', label: null, fraction: 1 });
  });
}
